import { useQuery } from "@apollo/client";
import { useMemo } from "react";
import {
  CoachingDashboardItemNode,
  CoachingDimensionNode,
} from "types/graphql-schema";

import Loading from "@components/loading/loading";

import { AgendaSection } from "./coaching-agenda";
import { DashboardGrid, MetaStats } from "./coaching-dashboard";
import { DimensionSection } from "./coaching-dimensions";
import { SourcesSection } from "./coaching-sources";
import getCoachingReportQuery from "./graphql/get-coaching-report-query";
import getNextOneononeForCoachingQuery from "./graphql/get-next-oneonone-for-coaching-query";

const SectionHeading = ({
  title,
  description,
}: {
  title: string;
  description?: string;
}) => (
  <div className="border-b border-gray-200 pb-4">
    <h2 className="text-lg font-semibold leading-7 text-gray-900">{title}</h2>
    {description && (
      <p className="mt-1 text-sm leading-6 text-gray-500">{description}</p>
    )}
  </div>
);

const CoachingPage = ({
  profileId,
  managerId,
}: {
  profileId: number;
  managerId: number;
}) => {
  const { data, loading } = useQuery(getCoachingReportQuery, {
    variables: { subjectId: profileId, managerId },
  });

  const startDatetime = useMemo(() => new Date().toISOString(), []);

  const { data: meetingData } = useQuery(getNextOneononeForCoachingQuery, {
    variables: {
      participants: [profileId, managerId],
      startDatetime,
    },
  });

  const nextMeetingId: number | null =
    meetingData?.meetings?.edges?.[0]?.node?.id ?? null;

  const report = data?.coachingReport;

  return (
    <div className="w-full py-6 pb-12 px-4 sm:px-6 lg:px-8">
      {loading ? (
        <Loading>Loading coaching report...</Loading>
      ) : !report ? (
        <div className="py-12 text-center text-sm text-gray-500">
          Coaching report not yet available.
        </div>
      ) : (
        <div className="flex flex-col gap-12">
          {/* Meta stats */}
          {report.meta && (
            <MetaStats
              meta={report.meta}
              sourceCount={(report.sources ?? []).length}
            />
          )}

          {/* Dashboard */}
          {report.dashboard && report.dashboard.length > 0 && (
            <div className="flex flex-col gap-5">
              <SectionHeading title="At a Glance" />
              <DashboardGrid cards={report.dashboard} />
            </div>
          )}

          {/* Dimension deep dives */}
          {report.dimensions && report.dimensions.length > 0 && (
            <div className="flex flex-col gap-5">
              <SectionHeading
                title="Dimension Deep Dives"
                description="Detailed analysis across each coaching dimension with evidence and suggested actions."
              />
              <div className="flex flex-col gap-10">
                {(() => {
                  const dashboardOrder = (report.dashboard ?? []).map(
                    (card: CoachingDashboardItemNode) => card.dimension,
                  );
                  const sorted = [...report.dimensions].sort(
                    (a: CoachingDimensionNode, b: CoachingDimensionNode) => {
                      const aIdx = dashboardOrder.indexOf(a.name);
                      const bIdx = dashboardOrder.indexOf(b.name);
                      if (aIdx === -1) return 1;
                      if (bIdx === -1) return -1;
                      return aIdx - bIdx;
                    },
                  );
                  return sorted.map((dim: CoachingDimensionNode, i: number) => (
                    <DimensionSection key={dim?.name ?? i} dimension={dim} />
                  ));
                })()}
              </div>
            </div>
          )}

          {/* Agenda */}
          {report.agenda && (
            <div className="flex flex-col gap-5">
              <SectionHeading
                title="Recommended Agenda"
                description="Prioritized topics for your upcoming conversations."
              />
              <AgendaSection
                nextMeeting={report.agenda.nextMeeting ?? []}
                phasedPlan={report.agenda.phasedPlan ?? []}
                nextMeetingId={nextMeetingId}
              />
            </div>
          )}

          {/* Sources */}
          {report.sources && report.sources.length > 0 && (
            <div className="flex flex-col gap-5">
              <SectionHeading
                title="Data Sources"
                description="All sources used to generate this coaching report."
              />
              <SourcesSection sources={report.sources} />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CoachingPage;
